import React, { useState, useEffect } from 'react'
import { Key, Copy, RefreshCw, AlertTriangle, Check } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
  Button,
  Input,
} from '../ui'
import { LoadingSpinner } from '../ui/loading-states'
import { apiClient } from '../../lib/api'
import type { Employee } from '../../hooks/useEmployees'

interface ResetPasswordDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  employee: Employee | null
}

function generatePassword(): string {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789'
  let password = ''
  for (let i = 0; i < 8; i++) {
    password += chars.charAt(Math.floor(Math.random() * chars.length))
  }
  return password
}

export function ResetPasswordDialog({
  open,
  onOpenChange,
  employee,
}: ResetPasswordDialogProps) {
  const [newPassword, setNewPassword] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    if (open) {
      setNewPassword(generatePassword())
      setError(null)
      setSuccess(false)
      setCopied(false)
    }
  }, [open])

  const handleClose = () => {
    if (!isLoading) {
      onOpenChange(false)
    }
  }

  const handleGeneratePassword = () => {
    setNewPassword(generatePassword())
    setCopied(false)
  }

  const handleCopyPassword = async () => {
    try {
      await navigator.clipboard.writeText(newPassword)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy password:', err)
    }
  }

  const handleResetPassword = async () => {
    if (!employee?.user?.id || !newPassword) return

    if (newPassword.length < 6) {
      setError('Пароль должен содержать минимум 6 символов')
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      await apiClient.resetUserPassword(employee.user.id, newPassword)
      setSuccess(true)
    } catch (err: unknown) {
      if (err instanceof Error) {
        setError(err.message)
      } else if (typeof err === 'object' && err !== null && 'message' in err) {
        setError(String((err as { message: unknown }).message))
      } else {
        setError('Не удалось сбросить пароль')
      }
    } finally {
      setIsLoading(false)
    }
  }

  const getEmployeeFullName = (emp: Employee) => {
    const parts = [emp.last_name, emp.first_name, emp.middle_name].filter(Boolean)
    return parts.join(' ')
  }

  if (!employee) return null

  if (!employee.user_id || !employee.user) {
    return (
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-[450px]">
          <DialogHeader>
            <DialogTitle>
              <div className="flex items-center gap-2">
                <Key className="h-5 w-5" />
                Сброс пароля
              </div>
            </DialogTitle>
            <DialogClose onClose={handleClose} />
          </DialogHeader>
          <div className="py-4">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5" />
                <div>
                  <p className="font-medium text-yellow-800">
                    Учётная запись не найдена
                  </p>
                  <p className="text-sm text-yellow-700 mt-1">
                    У сотрудника <strong>{getEmployeeFullName(employee)}</strong> нет
                    зарегистрированной учётной записи. Создайте приглашение для регистрации.
                  </p>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={handleClose}>Закрыть</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    )
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[450px]">
        <DialogHeader>
          <DialogTitle>
            <div className="flex items-center gap-2">
              <Key className="h-5 w-5" />
              Сброс пароля
            </div>
          </DialogTitle>
          <DialogDescription>
            Установите новый пароль для учётной записи сотрудника
          </DialogDescription>
          {!isLoading && !success && <DialogClose onClose={handleClose} />}
        </DialogHeader>

        {!success ? (
          <>
            <div className="space-y-4 py-4">
              <div className="bg-gray-50 rounded-lg p-3 space-y-1">
                <p className="text-sm text-gray-600">Сотрудник</p>
                <p className="font-medium">{getEmployeeFullName(employee)}</p>
                <p className="text-sm text-gray-500">
                  Табельный номер: {employee.employee_number}
                </p>
              </div>

              <div className="bg-blue-50 rounded-lg p-3 space-y-1">
                <p className="text-sm text-blue-600">Учётная запись</p>
                <p className="font-medium text-blue-900">{employee.user.email}</p>
              </div>

              <div className="space-y-2">
                <label htmlFor="newPassword" className="text-sm font-medium text-gray-700">
                  Новый пароль
                </label>
                <div className="flex gap-2">
                  <Input
                    id="newPassword"
                    type="text"
                    value={newPassword}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewPassword(e.target.value)}
                    placeholder="Введите новый пароль"
                    disabled={isLoading}
                    className="font-mono"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleGeneratePassword}
                    disabled={isLoading}
                    title="Сгенерировать новый пароль"
                  >
                    <RefreshCw className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleCopyPassword}
                    disabled={isLoading || !newPassword}
                    title="Скопировать пароль"
                  >
                    {copied ? (
                      <Check className="h-4 w-4 text-green-600" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </Button>
                </div>
                <p className="text-xs text-gray-500">
                  Минимум 6 символов. Нажмите кнопку обновления для генерации нового пароля.
                </p>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4 text-red-600" />
                    <p className="text-sm text-red-800">{error}</p>
                  </div>
                </div>
              )}

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 text-yellow-600 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-yellow-800">
                    <p className="font-medium">Важно:</p>
                    <ul className="mt-1 list-disc list-inside space-y-1">
                      <li>Передайте новый пароль сотруднику безопасным способом</li>
                      <li>Рекомендуйте сотруднику сменить пароль при первом входе</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={isLoading}
              >
                Отмена
              </Button>
              <Button
                type="button"
                onClick={handleResetPassword}
                disabled={isLoading || !newPassword || newPassword.length < 6}
              >
                {isLoading ? (
                  <>
                    <LoadingSpinner className="mr-2 h-4 w-4" />
                    Сброс...
                  </>
                ) : (
                  'Сбросить пароль'
                )}
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <div className="space-y-4 py-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center gap-3">
                  <div className="flex-shrink-0 w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                    <Check className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <p className="font-medium text-green-800">
                      Пароль успешно сброшен!
                    </p>
                    <p className="text-sm text-green-700 mt-1">
                      Новый пароль установлен для {employee.user.email}
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">
                  Новый пароль (скопируйте и передайте сотруднику)
                </label>
                <div className="flex gap-2">
                  <Input
                    type="text"
                    value={newPassword}
                    readOnly
                    className="font-mono bg-gray-50"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleCopyPassword}
                    title="Скопировать пароль"
                  >
                    {copied ? (
                      <Check className="h-4 w-4 text-green-600" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button onClick={handleClose}>Закрыть</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
